import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { Button } from "../../../components/ui/Button/Button";
import { analyticsWebsites } from "../../../api/analyticsWebsites";
import { analyticsKey, type AnalyticsScope } from "../queryKeys";
import type { DateRange, RefreshOperation } from "../types";
import { sourceLabel } from "../types";
import { errorMessage } from "../format";
import styles from "../Analytics.module.css";
function stored(key: string) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
const active = (operation: RefreshOperation | undefined) =>
  operation?.status === "queued" || operation?.status === "running";
export default function RefreshStatus({
  scope,
  id,
  range,
  canRefresh,
}: {
  scope: AnalyticsScope;
  id: string;
  range: DateRange;
  canRefresh: boolean;
}) {
  const client = useQueryClient();
  const storageKey = `analytics-refresh:${scope.userId}:${scope.organizationId ?? "personal"}:${id}`;
  const [operationId, setOperationId] = useState(() => stored(storageKey));
  const busy = useRef(false),
    requestId = useRef<string | null>(null);
  const requestRange = useRef<string | null>(null);
  const handled = useRef<string | null>(null);
  const query = useQuery({
    queryKey: analyticsKey(scope, "refresh", id, operationId),
    queryFn: ({ signal }) =>
      analyticsWebsites.refreshStatus(id, operationId!, signal),
    enabled: !!operationId,
    retry: false,
    refetchInterval: (query) =>
      query.state.status !== "error" && active(query.state.data) ? 2000 : false,
  });
  const refresh = useMutation({
    mutationFn: () => analyticsWebsites.refresh(id, range, requestId.current!),
    onSuccess: (data) => {
      setOperationId(data.id);
      try {
        sessionStorage.setItem(storageKey, data.id);
      } catch {
        /* Storage may be unavailable in private browsing. */
      }
      client.setQueryData(analyticsKey(scope, "refresh", id, data.id), data);
    },
    onSettled: () => {
      busy.current = false;
    },
  });
  const operation = query.data ?? refresh.data;
  useEffect(() => {
    if (
      !operation ||
      active(operation) ||
      handled.current === `${operation.id}:${operation.status}`
    )
      return;
    handled.current = `${operation.id}:${operation.status}`;
    requestId.current = null;
    client.invalidateQueries({ queryKey: analyticsKey(scope, "context", id) });
    client.invalidateQueries({ queryKey: analyticsKey(scope, "report", id) });
    client.invalidateQueries({ queryKey: analyticsKey(scope, "insights", id) });
    client.invalidateQueries({
      queryKey: analyticsKey(scope, "collection-report", id),
    });
  }, [operation, client, scope, id]);
  const loading = refresh.isPending || active(operation);
  const status =
    operation?.status === "completed"
      ? "Data updated"
      : operation?.status === "partial"
        ? "Some data updated"
        : operation?.status === "failed"
          ? "Refresh failed"
          : operation?.status === "running"
            ? "Refreshing data"
            : operation?.status === "queued"
              ? "Refresh queued"
              : null;
  return (
    <div className={styles.refresh}>
      <Button
        variant="secondary"
        icon={
          <RefreshCw size={16} className={loading ? styles.spin : undefined} />
        }
        disabled={!canRefresh || loading || (query.isPending && !!operationId)}
        loading={refresh.isPending}
        onClick={() => {
          if (busy.current || loading) return;
          busy.current = true;
          const selectedRange = JSON.stringify(range);
          if (requestRange.current !== selectedRange) {
            requestId.current = null;
            requestRange.current = selectedRange;
          }
          requestId.current ??= crypto.randomUUID();
          refresh.mutate();
        }}
      >
        {loading ? "Refreshing…" : "Refresh data"}
      </Button>
      {!canRefresh && <small>An owner or admin can refresh data.</small>}
      {status && (
        <details className={styles.refreshDetails}>
          <summary aria-live="polite">{status}</summary>
          <div>
            {operation?.start && operation.end && (
              <p>
                Requested period: {operation.start} – {operation.end}
                {operation.compare === "previous"
                  ? " and its previous period"
                  : ""}
              </p>
            )}
            {Object.entries(operation!.sources).map(([source, value]) => (
              <p key={source}>
                <strong>{sourceLabel[source as "ga4" | "gsc"]}:</strong>{" "}
                {value.status.replace(/_/g, " ")}
                {value.message ? ` · ${value.message}` : ""}
              </p>
            ))}
          </div>
        </details>
      )}
      {(refresh.isError || query.isError) && (
        <p className={styles.actionError} role="alert">
          {errorMessage(refresh.error ?? query.error)}
          {query.isError && (
            <button className={styles.textLink} onClick={() => query.refetch()}>
              Check progress again
            </button>
          )}
        </p>
      )}
    </div>
  );
}
